/**
 * Attendance Correction Dialog — creates a supplemental payslip when
 * a day was wrongly recorded as absent AFTER the payroll has been sent.
 *
 * Flow:
 *   1. Pick employee (search by employee_number OR name).
 *   2. Add one or more ISO dates that need to be corrected.
 *   3. Optionally flip attendance rows to "present" (default ON).
 *   4. Preview the total = N days × daily rate (pulled from the
 *      original payslip for each date's cycle).
 *   5. Commit → creates draft adjustment. Admin publishes via the
 *      standard per-row "Send" button on the payroll table.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { SpinnerGap, Fingerprint, Plus, X } from '@phosphor-icons/react';
import EmployeeSearchSelect from './EmployeeSearchSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    title: 'تصحيح بصمة على قسيمة مُصدَرة',
    desc:  'أعِد يوماً كان مسجَّلاً كغياب خطأً — يُنشئ النظام قسيمة تعديل تكميلية بقيمة اليوم من نفس القسيمة الأصلية، ويحدّث سجل الحضور.',
    employee: 'الموظف',
    dates: 'التواريخ المطلوب تصحيحها',
    addDate: 'إضافة تاريخ',
    reason: 'السبب / الملاحظات',
    reasonPh: 'مثال: نسي البصمة — التحقق تم من الكاميرا',
    updateAttendance: 'تحديث سجل الحضور تلقائياً (يُغيَّر إلى "حاضر")',
    commit: 'إنشاء قسيمة التصحيح',
    committing: 'جاري الإنشاء…',
    cancel: 'إلغاء',
    needDate: 'أضف تاريخاً واحداً على الأقل',
    needEmp:  'اختر موظفاً أولاً',
  },
  en: {
    title: 'Attendance Correction on Issued Payslip',
    desc:  'Restore a day that was wrongly marked as absent. The system creates a supplemental adjustment at the payslip\'s daily rate and updates attendance.',
    employee: 'Employee',
    dates: 'Dates to Correct',
    addDate: 'Add Date',
    reason: 'Reason / Notes',
    reasonPh: 'e.g. Missed clock-in — verified via camera',
    updateAttendance: 'Update attendance record automatically (mark as "present")',
    commit: 'Create Correction Payslip',
    committing: 'Creating…',
    cancel: 'Cancel',
    needDate: 'Add at least one date',
    needEmp:  'Select an employee first',
  },
};

export default function AttendanceCorrectionDialog({
  open, onOpenChange, employees, language, direction, onSuccess,
}) {
  const isAr = language === 'ar';
  const L = isAr ? T.ar : T.en;

  const [userId, setUserId] = useState('');
  const [dates, setDates] = useState([new Date().toISOString().slice(0, 10)]);
  const [reason, setReason] = useState('');
  const [updateAtt, setUpdateAtt] = useState(true);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (open) {
      setUserId('');
      setDates([new Date().toISOString().slice(0, 10)]);
      setReason('');
      setUpdateAtt(true);
    }
  }, [open]);

  const addDate = () => setDates([...dates, new Date().toISOString().slice(0, 10)]);
  const setDate = (idx, v) => setDates(dates.map((d, i) => (i === idx ? v : d)));
  const rmDate = (idx) => setDates(dates.filter((_, i) => i !== idx));

  const doCommit = async () => {
    if (!userId) { toast.error(L.needEmp); return; }
    const cleaned = dates.filter(Boolean);
    if (cleaned.length === 0) { toast.error(L.needDate); return; }
    setCommitting(true);
    try {
      const { data } = await axios.post(`${API}/payroll/attendance-correction`, {
        user_id: userId,
        dates: cleaned,
        reason,
        update_attendance: updateAtt,
      }, { withCredentials: true });
      toast.success(isAr
        ? `تم إنشاء قسيمة التصحيح كمسودة — ${data.total.toLocaleString()} ر.س (${data.items_count} يوم). اضغط "إرسال" في الجدول لإرسالها.`
        : `Correction draft created — ${data.total.toLocaleString()} SAR (${data.items_count} day(s)). Click "Send" on the row to notify.`);
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإنشاء' : 'Create failed'));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" dir={direction}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="att-correction-title">
            <Fingerprint size={20} weight="duotone" />
            {L.title}
          </DialogTitle>
          <DialogDescription>{L.desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>{L.employee}</Label>
            <EmployeeSearchSelect
              employees={employees}
              value={userId}
              onChange={setUserId}
              language={language}
              direction={direction}
              filterFn={(e) => e.role === 'employee'}
              testId="att-employee"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{L.dates}</Label>
              <Button
                type="button" variant="outline" size="sm"
                onClick={addDate} data-testid="att-add-date"
              >
                <Plus size={14} className="me-1" />
                {L.addDate}
              </Button>
            </div>
            <div className="space-y-2">
              {dates.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={d}
                    onChange={(e) => setDate(i, e.target.value)}
                    data-testid={`att-date-${i}`}
                  />
                  {dates.length > 1 && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => rmDate(i)} data-testid={`att-rm-${i}`}
                      className="text-red-600 hover:bg-red-50 shrink-0"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>{L.reason}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={L.reasonPh}
              data-testid="att-reason"
              rows={2}
            />
          </div>

          <label className="flex items-start gap-3 p-3 border border-[#C4BFF0] rounded cursor-pointer">
            <Checkbox
              checked={updateAtt}
              onCheckedChange={(v) => setUpdateAtt(!!v)}
              data-testid="att-update-flag"
            />
            <span className="text-xs">{L.updateAttendance}</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L.cancel}</Button>
          <Button
            onClick={doCommit}
            disabled={committing || !userId || dates.filter(Boolean).length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="att-commit-btn"
          >
            {committing
              ? <><SpinnerGap className="animate-spin" size={16} /> {L.committing}</>
              : L.commit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
